import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { useAuth } from '../auth/use-auth'
import { Brand } from '../components/brand'
import { Button } from '../components/button'

/**
 * /access-denied page.
 *
 * Per the MUR "Mockups" page wireframe: full-page state. Copy is verbatim. `role="alert"`
 * at page level. Primary CTA "Sign in as a different user" - implemented as
 * sign-out + immediate re-sign-in. Receives focus on load per the MUR "Design Tokens and Accessibility" page
 * accessibility guideline.
 *
 * No auto-redirect (per task brief). The user must explicitly act.
 */
export function AccessDeniedPage(): JSX.Element {
  const { signOut } = useAuth()
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const location = useLocation()
  const reason =
    (location.state as { reason?: 'tenant_mismatch' | 'role_missing' } | null)?.reason ?? null

  useEffect(() => {
    buttonRef.current?.focus()
  }, [])

  return (
    <div className="flex h-full flex-col">
      <header role="banner" className="flex h-14 items-center px-murmuration-6">
        <Brand to="/sign-in" variant="compact" />
      </header>
      <main
        role="main"
        id="main"
        className="mx-auto flex w-full max-w-[640px] flex-1 flex-col items-center justify-center gap-murmuration-4 px-murmuration-6 text-center"
      >
        <div
          role="alert"
          aria-labelledby="access-denied-heading"
          className="flex flex-col items-center gap-murmuration-3"
        >
          <h1 id="access-denied-heading" className="text-murmuration-h1 text-text-primary">
            Not authorised
          </h1>
          <p className="max-w-[480px] text-murmuration-body text-text-secondary">
            Your account is signed in but does not have permission to view Murmuration.
          </p>
          {reason && (
            <p className="text-murmuration-label text-text-muted">
              {reason === 'tenant_mismatch'
                ? 'Your account is not assigned to the Suits Me tenant.'
                : 'Your account is missing the staff role required to use Murmuration.'}
            </p>
          )}
        </div>
        <Button
          ref={buttonRef}
          variant="primary"
          onClick={() => {
            void signOut()
          }}
        >
          Sign in as a different user
        </Button>
        <p className="text-murmuration-label text-text-muted">
          If you should have access, contact IT.
        </p>
      </main>
    </div>
  )
}
